import React, { useState } from "react";
import { jwtDecode } from "jwt-decode"; // Biblioteca para ler o ID de dentro do Token
import { FreteAPI } from "../../../services/freteAPI";
import { paymentAPI } from "../../../services/paymentAPI";
import { useCart } from "../../../context/CartContext";
import styles from './frete.module.css';

function FretePage() {
    const [cep, setCep] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [comprador, setComprador] = useState({
        nome: '', email: '', cpf: '', telefone: '',
        logradouro: '', bairro: '', numero: '',
        cidade: '', uf: ''
    });

    const { cartItems, setShipping, selectedShipping, totalOrder } = useCart();

    // 1. Função para buscar endereço via CEP
    const preencherEndereco = async (cepDigitado) => {
        if (cepDigitado.length === 8) {
            try {
                const res = await fetch(`https://viacep.com.br/ws/${cepDigitado}/json/`);
                const data = await res.json();
                if (!data.erro) {
                    setComprador(prev => ({
                        ...prev,
                        logradouro: data.logradouro,
                        bairro: data.bairro,
                        cidade: data.localidade,
                        uf: data.uf
                    }));
                }
            } catch (err) { 
                console.error("Erro ViaCEP", err); 
            }
        }
    };

    // 2. Calcula opções de frete
    const handleCalcular = async () => {
        if (cep.length !== 8) return alert('CEP INVÁLIDO');
        setLoading(true);
        try {
            const payload = {
                cep: cep,
                produtos: cartItems.map(item => ({
                    id: item.product_details?.id || item.id,
                    largura: 15, altura: 15, comprimento: 15, weight: 0.5,
                    insurance_value: item.price || item.product_details?.price || 0,
                    quantity: item.quantity
                }))
            };
            const response = await FreteAPI.findShip(payload);
            setOptions(response);
        } catch (err) { 
            alert("Erro ao calcular frete."); 
        } finally { 
            setLoading(false); 
        }
    };

    // 3. Finaliza o pagamento capturando o ID do Token
    const handleFinalizarPagamento = async () => {
        // Validação de campos obrigatórios
        if (!comprador.nome || !comprador.cpf || !selectedShipping || !comprador.cidade) {
            return alert("Preencha todos os dados de entrega e selecione o frete.");
        }

        // --- LÓGICA DO TOKEN ---
        const token = localStorage.getItem("token"); // Ajuste para a chave que você usa (ex: 'auth_token')
        
        if (!token) {
            return alert("Você precisa estar logado para finalizar a compra.");
        }

        let userId;
        try {
            const decoded = jwtDecode(token);
            // Tenta pegar 'id' ou 'sub' (padrão JWT)
            userId = decoded.id || decoded.sub || decoded.user_id; 
            
            if (!userId) throw new Error("ID não encontrado no token");
        } catch (error) {
            console.error("Erro ao decodificar token:", error);
            return alert("Sessão inválida. Por favor, faça login novamente.");
        }
        // -----------------------

        setLoading(true);
        try {
            const payload = {
                user_id: userId, // ID DINÂMICO AQUI
                cliente: {
                    name: comprador.nome,
                    email: comprador.email,
                    cpf: comprador.cpf,
                    phone: comprador.telefone
                },
                endereco: {
                    cep,
                    street: comprador.logradouro,
                    neighborhood: comprador.bairro,
                    number: comprador.numero,
                    city: comprador.cidade,
                    state: comprador.uf,
                    nome: comprador.nome,
                    cpf: comprador.cpf,
                    telefone: comprador.telefone
                },
                frete: {
                    id: selectedShipping.id,
                    name: selectedShipping.name,
                    price: selectedShipping.price
                },
                itens: cartItems.map(item => ({
                    product_id: item.id || item.product_details?.id,
                    name: item.name || item.product_details?.name,
                    price: item.price || item.product_details?.price,
                    quantity: item.quantity
                })),
                total: totalOrder
            };

            const res = await paymentAPI.create_link(payload);
            const url = res.data?.url || res.url;
            if (url) window.location.href = url;
        } catch (err) {
            alert("Erro ao processar pedido.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Finalizar Pedido<span>.</span></h1>

            <div className={styles.mainGrid}>
                <div className={styles.formSection}>
                    <div className={styles.card}>
                        <h3>Dados Pessoais</h3>
                        <input 
                            placeholder="Nome Completo" 
                            className={styles.input} 
                            onChange={e => setComprador({ ...comprador, nome: e.target.value })} 
                        />
                        <div className={styles.row}>
                            <input 
                                placeholder="E-mail" 
                                className={styles.input} 
                                onChange={e => setComprador({ ...comprador, email: e.target.value })} 
                            />
                            <input 
                                placeholder="Telefone" 
                                className={styles.input} 
                                onChange={e => setComprador({ ...comprador, telefone: e.target.value })} 
                            />
                        </div>
                        <input 
                            placeholder="CPF (apenas números)" 
                            className={styles.input} 
                            onChange={e => setComprador({ ...comprador, cpf: e.target.value.replace(/\D/g, '') })} 
                        />
                    </div>

                    <div className={styles.card}>
                        <h3>Endereço de Entrega</h3>
                        <div className={styles.cepRow}>
                            <input
                                placeholder="CEP" 
                                className={styles.input} 
                                value={cep} 
                                maxLength={8}
                                onChange={e => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setCep(val);
                                    if (val.length === 8) preencherEndereco(val);
                                }}
                            />
                            <button className={styles.calcBtn} onClick={handleCalcular} disabled={loading}>
                                {loading ? '...' : 'Calcular Frete'}
                            </button>
                        </div>
                        <div className={styles.row}>
                            <input 
                                placeholder="Rua" 
                                value={comprador.logradouro} 
                                className={styles.input} 
                                style={{ flex: 3 }} 
                                onChange={e => setComprador({ ...comprador, logradouro: e.target.value })} 
                            />
                            <input 
                                placeholder="Nº" 
                                className={styles.input} 
                                style={{ flex: 1 }} 
                                onChange={e => setComprador({ ...comprador, numero: e.target.value })} 
                            />
                        </div>
                        <div className={styles.row}>
                            <input 
                                placeholder="Cidade" 
                                value={comprador.cidade} 
                                className={styles.input} 
                                style={{ flex: 2 }} 
                                onChange={e => setComprador({ ...comprador, cidade: e.target.value })} 
                            />
                            <input 
                                placeholder="UF" 
                                value={comprador.uf} 
                                className={styles.input} 
                                style={{ flex: 1 }} 
                                onChange={e => setComprador({ ...comprador, uf: e.target.value })} 
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.shippingSection}>
                    <div className={styles.card}>
                        <h3>Opções de Frete</h3>
                        {options.map(opt => (
                            <div 
                                key={opt.id} 
                                className={`${styles.shippingOption} ${selectedShipping?.id === opt.id ? styles.active : ''}`} 
                                onClick={() => setShipping(opt)}
                            >
                                <span>{opt.name} ({opt.delivery} dias)</span>
                                <strong>R$ {opt.price.toFixed(2)}</strong>
                            </div>
                        ))}
                        
                        {selectedShipping && (
                            <div className={styles.totalBox}>
                                <p>Total: <strong>R$ {totalOrder.toFixed(2)}</strong></p>
                                <button className={styles.payBtn} onClick={handleFinalizarPagamento} disabled={loading}>
                                    {loading ? 'PROCESSANDO...' : 'PAGAR AGORA'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FretePage;